import { Dialog } from '@angular/cdk/dialog';
import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRouteSnapshot, Event, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { Observable } from 'rxjs';
import { filter } from 'rxjs/operators';

import { PublicRouteData } from '~core/navigation/route-meta';
import { AuthService } from '~features/auth/auth.service';
import { ConfirmationBannerComponent } from '~features/auth/confirmation-banner/confirmation-banner.component';
import { BrevoAutoLoginRouterService } from '~features/brevo-auto-login/brevo-auto-login-router.service';
import { CompanyInfoService } from '~features/company/company-info.service';
import { CompanylistService } from '~features/company/companylist.service';
import { ExchangeMappingService } from '~features/company/exchangeMapping.service';
import { ContactBannerComponent } from '~features/contact-banner/contact-banner.component';
import { CookieToastComponent } from '~features/cookies/cookie-toast/cookie-toast.component';
import { CookieService } from '~features/cookies/cookie.service';
import { DemoEnvironmentBannerComponent } from '~features/demo-environment/demo-environment-banner/demo-environment-banner.component';
import { EventService } from '~features/events/event.service';
import { PendingEventAttendanceDialogComponent } from '~features/events/pending-event/pending-event-attendance-dialog/pending-event-attendance-dialog.component';
import { FrontendConfigService } from '~features/frontend-config/frontendConfig.service';
import { GlobalPodcastPlayerComponent } from '~features/podcasts/global-podcast-player/global-podcast-player.component';
import { CanonicalLinkService } from '~features/seo/canonical-link.service';
import { TransactionbannerComponent } from '~features/transactions/transactionbanner/transactionbanner.component';
import { PublicPageShellComponent } from '~ui-store/composed/public-page-shell/public-page-shell.component';
import { UiToastSurfaceComponent } from '~ui-store/primitives';
import { UserBehaviorAnalyticsService } from '~features/user-behavior-analytics/user-behavior-analytics.service';
import { UserNotificationToastComponent } from '~features/user-notifications/user-notification-toast/user-notification-toast.component';
import { UserReminderToastComponent } from '~features/user-reminder/user-reminder-toast/user-reminder-toast.component';
import { UserReminderService } from '~features/user-reminder/user-reminder.service';
import { AdminFormRequestMessageComponent } from '~shared/components/ui/admin-form-request-message/admin-form-request-message.component';
import { SimpleLoadingIndicatorComponent } from '~shared/components/ui/simple-loading-indicator/simple-loading-indicator.component';
import { DialogService } from '~shared/services/dialog.service';
import { PosthogService } from '~shared/services/posthog.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
  imports: [
    PublicPageShellComponent,
    RouterOutlet,
    SimpleLoadingIndicatorComponent,
    TransactionbannerComponent,
    ConfirmationBannerComponent,
    ContactBannerComponent,
    UserNotificationToastComponent,
    UserReminderToastComponent,
    CookieToastComponent,
    GlobalPodcastPlayerComponent,
    AdminFormRequestMessageComponent,
    DemoEnvironmentBannerComponent,
    UiToastSurfaceComponent,
    PendingEventAttendanceDialogComponent,
  ],
})
export class AppComponent implements OnInit {
  title = 'researchHub';
  currentURL = '';
  showContactButton = false;
  showContactSlider = false;
  public isCookiesAccepted = false;
  showHeaderAndFooter = true;
  showSlimHeader = false;
  showFooter = true;
  waitForCompanyList = false;

  companyListService = inject(CompanylistService);
  comanyListIsLoaded = this.companyListService.isLoaded;
  exchangeMappingService = inject(ExchangeMappingService);
  frontendConfigService = inject(FrontendConfigService);
  eventService = inject(EventService);
  dialog = inject(Dialog);
  dialogService = inject(DialogService);
  frontendConfig = this.frontendConfigService.getFrontendConfig();
  navigationEnd: Observable<NavigationEnd>;
  canonicalLinkService = inject(CanonicalLinkService);
  postHogService = inject(PosthogService);
  public showCookieToast = this.cookieService.showCookieToast;
  private HEADER_OFFSET = 500; // 1 em = 16px =>  without banner: 25em = 400px; with banner = 28.5em = 456px; new company slider adds 53.3 px
  private authService = inject(AuthService);
  private userReminderService = inject(UserReminderService);
  private newsletterTrackingRouterService = inject(BrevoAutoLoginRouterService);

  constructor(
    private router: Router,
    private cookieService: CookieService,
    private companyInfoService: CompanyInfoService
  ) {
    this.navigationEnd = router.events.pipe(
      filter((event: Event) => event instanceof NavigationEnd)
    ) as Observable<NavigationEnd>;
    this.userReminderService.checkReminder();
  }
  userBehaviorAnalyticsService = inject(UserBehaviorAnalyticsService);
  ngOnInit() {
    this.cookieService.initPrivacyPermissionsAndCookies();
    this.applyLayoutModeForCurrentRoute();

    // CONSOLIDATED NAVIGATION EVENT HANDLER - All router logic in one place
    this.router.events
      .pipe(filter((event: Event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.handleNavigationEnd(event);
      });

    // Handle tab close/refresh for dwell time tracking
    window.addEventListener('beforeunload', () => {
      this.userBehaviorAnalyticsService.trackFinalPageDwell();
    });
  }

  /**
   * Consolidated navigation handler - all navigation logic in one place
   * This replaces multiple separate router subscriptions throughout the app
   */
  private handleNavigationEnd(event: NavigationEnd): void {
    const url = event.urlAfterRedirects || event.url;

    // 1. UBA Analytics - Track page views (replaces setupGlobalTracking)
    this.trackPageViewForAnalytics(event, url);

    // 2. PostHog - Track page views (only if cookies accepted)
    if (this.cookieService.isCookiesAccepted()) {
      this.postHogService.trackPageView(url);
    }

    // 3. Layout mode - Toggle from route data instead of URL string matching
    this.applyLayoutModeForCurrentRoute();

    // 4. Canonical URLs - Update SEO canonical links (replaces canonical service subscription)
    this.updateCanonicalUrl();

    // 5. Brevo Auto-Login - Handle dialog logic (replaces brevo service subscription)
    this.handleBrevoDialogLogic(event);
  }

  /**
   * Handle UBA page view tracking (replaces setupGlobalTracking in UBA service)
   */
  private trackPageViewForAnalytics(event: NavigationEnd, url: string): void {
    const companySlug = this.userBehaviorAnalyticsService.extractCompanySlug(this.router.routerState.snapshot.root);

    // Handle page transition for dwell time tracking
    this.userBehaviorAnalyticsService.handlePageTransition(url, companySlug);

    // Track page view with referrer
    this.userBehaviorAnalyticsService.trackEvent('page_view', {
      url,
      companySlug,
      referrer: document.referrer || 'direct',
      title: document.title,
    });
  }

  /**
   * Update canonical URL for SEO (replaces canonical service router subscription)
   */
  private updateCanonicalUrl(): void {
    const canonicalUrl = this.canonicalLinkService.getCanonicalUrl();
    this.canonicalLinkService.setCanonicalUrl(canonicalUrl);
  }

  private applyLayoutModeForCurrentRoute(): void {
    const routeData = this.getDeepestRouteData(this.router.routerState.snapshot.root);
    const layoutMode = routeData?.layout ?? 'public';

    // Full chrome (nav header + banners/toasts) only on the standard public
    // layout. The slim layout shows a logo-only header instead, and the footer
    // stays on for everything except the fully bare minimal layout.
    this.showHeaderAndFooter = layoutMode === 'public';
    this.showSlimHeader = layoutMode === 'public-slim';
    this.showFooter = layoutMode !== 'public-minimal';
    this.waitForCompanyList = routeData?.waitForCompanyList ?? false;
  }

  private getDeepestRouteData(route: ActivatedRouteSnapshot): PublicRouteData | undefined {
    let currentRoute: ActivatedRouteSnapshot | null = route;
    let data: PublicRouteData | undefined;

    while (currentRoute) {
      data = currentRoute.data as PublicRouteData;
      currentRoute = currentRoute.firstChild ?? null;
    }

    return data;
  }

  /**
   * Handle Brevo auto-login dialog logic (moved from brevo service)
   */
  private handleBrevoDialogLogic(event: NavigationEnd): void {
    const currentUrl = event.url;
    const urlParams = new URLSearchParams(window.location.search);

    // Skip dialog for specific routes
    if (this.shouldSkipDialog(currentUrl)) {
      return;
    }

    const hash = urlParams.get('hash');
    const brevo = urlParams.get('brevo');

    // Scenario 1: Both hash and brevo are present and hash has content
    if (hash && hash.trim() !== '' && brevo === 'true') {
      // Process the tracking data
      this.newsletterTrackingRouterService.processNewsletterTracking(hash);

      // Clean up URL parameters
      this.cleanupBrevoUrlParameters(currentUrl);
    }
    // Scenario 2: No hash (or empty hash) but brevo is present
    else if (brevo === 'true') {
      this.cleanupBrevoUrlParameters(currentUrl);

      if (!hash || hash.trim() === '') {
        this.newsletterTrackingRouterService.needsEmptyHashDialog.set(true);
      }
    }
    // Scenario 3: Neither hash nor brevo present - simplified logic
    else {
      // For unauthenticated users, set flag to show dialog
      if (!this.authService.isAuthenticated()) {
        this.newsletterTrackingRouterService.needsNoParamsDialog.set(true);
      }
    }
  }

  /**
   * Check if dialog should be skipped for specific routes
   */
  private shouldSkipDialog(url: string): boolean {
    return (
      url.includes('/datenschutz') ||
      url.includes('/impressum') ||
      url.includes('/disclaimer') ||
      url.includes('/mifid2') ||
      url.includes('/setnewpassword') ||
      url.includes('/requestnewpassword') ||
      url.includes('/signup') ||
      url.includes('/admin') ||
      url.includes('/events/registration/') ||
      url.includes('/events/invite/')
    );
  }

  /**
   * Clean up Brevo tracking parameters from URL
   */
  private cleanupBrevoUrlParameters(currentUrl: string): void {
    const newUrl = new URL(currentUrl, window.location.origin);
    newUrl.searchParams.delete('hash');
    newUrl.searchParams.delete('brevo');

    const newPath = newUrl.pathname;
    const remainingParams = newUrl.searchParams.toString();
    const finalUrl = remainingParams ? `${newPath}?${remainingParams}` : newPath;

    // Update URL without triggering navigation event
    const location = this.newsletterTrackingRouterService.getLocation();
    location.replaceState(finalUrl);
  }

  isAdmin() {
    return this.authService.isAdmin();
  }
}
